"use client";

import Link from "next/link";
import { LifeBuoy, Loader2, LogIn, RefreshCw, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { GuestClaimErrorKind } from "@/lib/payments/guest-claim-errors";

export function GuestClaimRecovery({
  isClaiming,
  error,
  copy,
  onRetry,
  onSwitchAccount,
  signInPath,
  supportPath,
}: {
  isClaiming: boolean;
  error: GuestClaimErrorKind | null;
  copy: {
    claiming: string;
    errors: Record<GuestClaimErrorKind, { title: string; body: string }>;
    retry: string;
    switchAccount: string;
    signIn: string;
    contactSupport: string;
  };
  onRetry: () => void;
  onSwitchAccount: () => void;
  signInPath: string;
  supportPath: string;
}) {
  const errorCopy = error ? copy.errors[error] : null;

  return (
    <section
      aria-live="polite"
      aria-labelledby="guest-claim-title"
      className="rounded-2xl border border-status-warning-border bg-status-warning-bg p-4 sm:p-5"
    >
      <h2
        id="guest-claim-title"
        className="text-base font-semibold text-text-heading"
      >
        {errorCopy?.title ?? copy.claiming}
      </h2>
      <p className="mt-1 text-sm leading-6 text-text-body">
        {errorCopy?.body ?? copy.claiming}
      </p>
      {error === "temporary" ? (
        <Button
          type="button"
          onClick={onRetry}
          disabled={isClaiming}
          className="mt-4 min-h-11 gap-2 bg-brand-primary text-white hover:bg-brand-primary-hover"
        >
          {isClaiming ? (
            <Loader2
              className="size-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : (
            <RefreshCw className="size-4" aria-hidden="true" />
          )}
          {copy.retry}
        </Button>
      ) : null}
      {error === "account_conflict" ? (
        <Button
          type="button"
          onClick={onSwitchAccount}
          disabled={isClaiming}
          className="mt-4 min-h-11 gap-2 bg-brand-primary text-white hover:bg-brand-primary-hover"
        >
          {isClaiming ? (
            <Loader2
              className="size-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : (
            <Users className="size-4" aria-hidden="true" />
          )}
          {copy.switchAccount}
        </Button>
      ) : null}
      {error === "authentication" ? (
        <Button asChild className="mt-4 min-h-11 gap-2">
          <Link href={signInPath}>
            <LogIn className="size-4" aria-hidden="true" />
            {copy.signIn}
          </Link>
        </Button>
      ) : null}
      {error === "invalid" ? (
        <Button asChild className="mt-4 min-h-11 gap-2">
          <a href={supportPath}>
            <LifeBuoy className="size-4" aria-hidden="true" />
            {copy.contactSupport}
          </a>
        </Button>
      ) : null}
    </section>
  );
}
